import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, ReferenceLine,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from 'recharts';
import { fmtMoneyShort, fmtMoney, fmtPct } from '../engine.js';
import { CAT_COLORS } from '../data.js';
import { resolveTrendVisual } from '../components/formatAmount.js';

const TOOLTIP_CLS = 'bg-[rgba(18,21,28,0.97)] border border-border-strong rounded-md px-[0.7rem] py-2 min-w-[180px] text-[0.8rem] text-text shadow-[0_8px_24px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.4)] backdrop-blur-sm';

const axisProps = {
  tick: { fill: 'var(--text-muted)', fontSize: 11 },
  stroke: 'var(--border)',
};
const gridProps = { stroke: 'var(--grid)', strokeDasharray: '3 3' };
/* Recharts tooltip theming — dark bg + strong shadow + concrete hex fallback
   so any host page without CSS vars still renders dark (fixes white glare on
   FamilyCompositionChart et al). */
const tooltipProps = {
  contentStyle: {
    background: 'rgba(26,30,39,0.97)',
    border: '1px solid #3a4050',
    borderRadius: 6,
    color: '#e7ecf3',
    fontSize: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4)',
    backdropFilter: 'blur(6px)',
    padding: '8px 10px',
  },
  labelStyle: { color: '#8b93a7', fontWeight: 600, marginBottom: 4 },
  itemStyle:  { color: '#e7ecf3' },
  cursor: { fill: 'rgba(255,255,255,0.05)' },
  formatter: (v) => fmtMoney(v, { decimals: 0 }),
};

/* Cash Trajectory — line chart: liquid + short-term debt + net worth proxy */
export function TrajectoryChart({ data, height = 300 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
        <Tooltip {...tooltipProps} />
        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-muted)' }} />
        <ReferenceLine y={0} stroke="var(--border-strong)" strokeDasharray="3 3" />
        <Line type="monotone" dataKey="liquidEnding" name="Liquid cash"    stroke="#4ade80" strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="ccDebtEnding" name="Short-term debt" stroke="#f87171" strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="custodyHeldByJM" name="Custody reserve" stroke="#60a5fa" strokeWidth={2} strokeDasharray="4 2" dot={false} />
        <Line type="monotone" dataKey="fvfCumRevenue" name="Subscription cum rev" stroke="#fbbf24" strokeWidth={2} strokeDasharray="2 2" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

/* Monthly Cash Flow — bar chart: inflow vs outflow stacked */
export function CashFlowChart({ data, height = 280 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
        <Tooltip {...tooltipProps} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine y={0} stroke="var(--border-strong)" />
        <Bar dataKey="grossIn"  name="Gross cash in" fill="#4ade80" />
        <Bar dataKey="baseBurn" name="Base burn"     fill="#f87171" />
        <Line type="monotone" dataKey="netFCF" name="Net FCF" stroke="#fbbf24" strokeWidth={2} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Support AP composition — stacked bar. */
export function FamilyCompositionChart({ data, height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 20, bottom: 0, left: 60 }}>
        <CartesianGrid {...gridProps} horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
        <YAxis type="category" dataKey="label" {...axisProps} width={80} />
        <Tooltip {...tooltipProps} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="cashMari"      stackId="a" name="Demo Bank cash"       fill="#f472b6" />
        <Bar dataKey="groceries"     stackId="a" name="CC BDO groceries"     fill="#fbbf24" />
        <Bar dataKey="sundry"        stackId="a" name="CC PNB utilities"     fill="#fb923c" />
        <Bar dataKey="marian"        stackId="a" name="Routed channel"       fill="#e879f9" />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Recurring by Category — donut. */
export function RecurringByCategoryChart({ data, height = 280 }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const pieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const pct = total > 0 ? d.value / total : 0;
    return (
      <div className={TOOLTIP_CLS}>
        <strong className="block mb-1 text-accent-gold">{d.name}</strong>
        <div className="tabular-nums text-[0.8rem]">{fmtMoney(d.value, { decimals: 0 })}</div>
        <div className="text-[0.7rem] text-text-muted m-0 mb-[0.35rem]">{fmtPct(pct)} of total · {fmtMoney(total, { decimals: 0 })}</div>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Tooltip
          content={pieTooltip}
          cursor={false}
          wrapperStyle={{ outline: 'none', pointerEvents: 'none' }}
          allowEscapeViewBox={{ x: false, y: false }}
          isAnimationActive={false}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="bottom" />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={55}
          outerRadius={95}
          paddingAngle={2}
          isAnimationActive={false}
        >
          {data.map((d) => (
            <Cell key={d.name} fill={CAT_COLORS[d.name] || '#94a3b8'} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

/* v5 — Burn composition stacked */
export function BurnStackedChart({ data, height = 280 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
        <Tooltip {...tooltipProps} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="subs"          stackId="b" name="Recurring subs"  fill="#60a5fa" />
        <Bar dataKey="familyDirect"  stackId="b" name="Direct support"  fill="#fbbf24" />
        <Bar dataKey="marianAP"      stackId="b" name="Routed channel"  fill="#fb923c" />
        <Bar dataKey="mortgages"     stackId="b" name="Mortgages"       fill="#a78bfa" />
        <Bar dataKey="fuel"          stackId="b" name="Fuel"            fill="#f87171" />
        <Bar dataKey="seasonal"      stackId="b" name="Seasonal"        fill="#f472b6" />
        <Bar dataKey="lifestyle"     stackId="b" name="Lifestyle"       fill="#34d399" />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Net Worth Trajectory — area */
export function NetWorthChart({ data, height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="nwFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#fbbf24" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#fbbf24" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="label" {...axisProps} />
        <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
        <Tooltip {...tooltipProps} />
        <ReferenceLine y={0} stroke="var(--border-strong)" strokeDasharray="3 3" />
        <Area type="monotone" dataKey="netWorthProxy" name="Net worth proxy" stroke="#fbbf24" fill="url(#nwFill)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* Reserve milestone progress — radial-like bar */
export function ReserveMilestoneProgress({ progress }) {
  if (!progress) return null;
  const pct = Math.round(progress.pctComplete * 100);
  return (
    <div className="bg-gradient-to-br from-[rgba(240,180,41,0.08)] to-[rgba(244,114,182,0.06)] border border-[rgba(240,180,41,0.25)] rounded-[10px] px-5 py-[1.1rem]">
      <div className="flex justify-between items-baseline mb-[0.8rem] font-semibold text-accent-gold tracking-[0.02em]">
        <span>Reserve milestone</span>
        <span className="text-[0.8rem] text-text-muted font-normal">Target: {progress.targetEvent}</span>
      </div>
      <div className="w-full h-[22px] bg-surface-raised border border-border rounded-xl overflow-hidden mb-[0.8rem]">
        <div
          className="h-full bg-gradient-to-r from-accent-gold to-accent-pink transition-[width] duration-[400ms] flex items-center justify-end pr-2 min-w-[32px]"
          style={{ width: `${pct}%` }}
        >
          <span className="text-[0.75rem] font-semibold text-[#0b0d12] tracking-[0.03em]">{pct}%</span>
        </div>
      </div>
      <div className="flex justify-between flex-wrap gap-2 text-[0.85rem] text-text">
        <span><strong>{fmtMoney(progress.savedToDate, { decimals: 0 })}</strong> saved</span>
        <span>of <strong>{fmtMoney(progress.totalPlanned, { decimals: 0 })}</strong> target</span>
        <span className="text-text-muted">{progress.monthsRemaining} months remaining</span>
      </div>
    </div>
  );
}

/* ============================================================
   v7 — KPI sparkline · runway gauge · AR/AP stacked
   ============================================================ */

const SPARK_COLOR = {
  good: '#4ade80',
  bad:  '#f87171',
  warn: '#f0b429',
  neutral: '#60a5fa',
};
/* KPISparkline — tab-overview KPI card sparkline.
   As of the app-wide sparkline policy, this component:
   - Accepts `gradient` (boolean) matching the global scenario.sparklineGradient
   - Accepts `series` + `betterWhen` + `semanticTone` so it can compute its
     own tone via resolveTrendVisual — same path tables use.
   - Falls back to the legacy `data` + `tone` props for existing callers
     that don't want to pass the computed bundle.

   When both series+semanticTone are supplied, the computed tone overrides
   the explicit `tone` prop — that way KPI cards automatically honor the
   user's ScenarioBar toggles without each callsite remembering to. */
export function KPISparkline({
  data, series, tone = 'neutral', betterWhen, semanticTone, gradient = false,
  width = 80, height = 20,
}) {
  const rawSeries = Array.isArray(series) ? series
    : Array.isArray(data) ? data.map(d => (typeof d === 'object' ? d?.value : d))
    : null;
  if (!Array.isArray(rawSeries) || rawSeries.filter(v => Number.isFinite(v)).length < 2) {
    return <span className="inline-block align-middle bg-surface-raised rounded-[2px] opacity-40" style={{ width, height }} aria-hidden />;
  }
  /* Compute effective tone from the data when the caller gave us enough
     signal. Otherwise fall back to the explicit tone prop. */
  let effectiveTone = tone;
  let plotSeries = rawSeries;
  if (betterWhen != null || semanticTone != null) {
    const trend = resolveTrendVisual(rawSeries, 'currency', betterWhen, !!semanticTone);
    effectiveTone = trend.tone;
    plotSeries = trend.series;
  }
  const stroke = SPARK_COLOR[effectiveTone] ?? SPARK_COLOR.neutral;
  const gradId = React.useId();
  const chartData = plotSeries.map((v, i) => ({ i, value: v }));
  return (
    <div className="inline-block align-middle" style={{ width, height }} aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 1, right: 1, bottom: 1, left: 1 }}>
          {gradient && (
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={stroke} stopOpacity={0.35} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={1.6}
            fill={gradient ? `url(#${gradId})` : 'none'}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RunwayGauge({ months, max = 24, height = 120 }) {
  const safeMonths = Number.isFinite(months) ? months : max;
  const clamped = Math.min(max, Math.max(0, safeMonths));
  const color =
    safeMonths >= 12 ? '#4ade80'
    : safeMonths >= 6  ? '#f0b429'
    : '#f87171';
  const data = [{ name: 'runway', value: clamped, fill: color }];
  const display = Number.isFinite(months) ? `${months}` : `>${max}`;
  return (
    <div className="relative flex flex-col items-center gap-[0.1rem] min-w-[130px]">
      <ResponsiveContainer width="100%" height={height}>
        <RadialBarChart
          cx="50%" cy="80%"
          innerRadius="80%" outerRadius="100%"
          startAngle={180} endAngle={0}
          data={data}
        >
          <PolarAngleAxis type="number" domain={[0, max]} tick={false} axisLine={false} />
          <RadialBar dataKey="value" cornerRadius={6} background={{ fill: 'var(--surface-raised)' }} isAnimationActive={false} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-x-0 bottom-[0.4rem] text-center flex flex-col leading-[1.05]">
        <strong className="text-[1.6rem] tabular-nums" style={{ color }}>{display}</strong>
        <em className="text-[0.65rem] text-text-muted not-italic uppercase tracking-[0.05em]">months runway</em>
      </div>
    </div>
  );
}

export function ARAPStackedChart({ data, stackKeys, height = 220 }) {
  const ttip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((s, p) => s + (p.value || 0), 0);
    return (
      <div className={TOOLTIP_CLS}>
        <strong className="block mb-1 text-accent-gold">{label}</strong>
        {payload.map((p) => (
          <div key={p.dataKey} className="tabular-nums text-[0.8rem]" style={{ color: p.color }}>
            {p.name}: {fmtMoney(p.value, { decimals: 0 })}
          </div>
        ))}
        <div className="text-[0.7rem] text-text-muted m-0 mb-[0.35rem]">Total: {fmtMoney(total, { decimals: 0 })}</div>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 20, bottom: 0, left: 80 }}>
        <CartesianGrid {...gridProps} horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
        <YAxis type="category" dataKey="counterparty" {...axisProps} width={120} />
        <Tooltip content={ttip} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {stackKeys.map((sk) => (
          <Bar key={sk.key} dataKey={sk.key} stackId="arap" name={sk.name} fill={sk.fill} isAnimationActive={false} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
